import { useEffect, useMemo, useState } from "react";
import type { DiscardedFilter, NecrologiSearchQuery, NecrologiSearchResponse } from "../../../api/contract";
import { ApiRequestError, listNecrologi } from "../../api";

export const NECROLOGI_PAGE_SIZE = 50;

interface Filters {
  provincia: string;
  comune: string;
  q: string;
  source: string;
  vedovoOnly: boolean;
  casaVuotaOnly: boolean;
  discarded: DiscardedFilter;
}

export function useNecrologiSearch({ provincia, comune, q, source, vedovoOnly, casaVuotaOnly, discarded }: Filters) {
  const [text, setText] = useState(() => ({ q: q.trim(), comune: comune.trim() }));
  const [data, setData] = useState<NecrologiSearchResponse | null>(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setText({ q: q.trim(), comune: comune.trim() }), 300);
    return () => clearTimeout(timer);
  }, [q, comune]);

  const filter = useMemo<NecrologiSearchQuery>(() => ({
    provincia: provincia || undefined,
    comune: text.comune || undefined,
    q: text.q || undefined,
    source: source || undefined,
    vedovo: vedovoOnly || undefined,
    casaVuota: casaVuotaOnly || undefined,
    discarded,
  }), [provincia, text.comune, text.q, source, vedovoOnly, casaVuotaOnly, discarded]);
  const filterKey = JSON.stringify(filter);
  const [page, setPage] = useState({ filterKey, offset: 0 });
  const offset = page.filterKey === filterKey ? page.offset : 0;

  if (page.filterKey !== filterKey) setPage({ filterKey, offset: 0 });

  useEffect(() => {
    const controller = new AbortController();
    setBusy(true);
    setErr(null);
    listNecrologi({ ...filter, limit: NECROLOGI_PAGE_SIZE, offset }, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        const lastOffset = Math.max(0, Math.ceil(res.total / NECROLOGI_PAGE_SIZE) - 1) * NECROLOGI_PAGE_SIZE;
        if (offset > lastOffset) {
          setPage({ filterKey, offset: lastOffset });
          return;
        }
        setData(res);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setErr(error instanceof ApiRequestError ? error.message : "Errore caricamento");
      })
      .finally(() => {
        if (!controller.signal.aborted) setBusy(false);
      });
    return () => controller.abort();
  }, [filter, offset, reloadKey]);

  return {
    data, setData, busy, err, offset,
    setOffset: (next: number) => setPage({ filterKey, offset: Math.max(0, next) }),
    reload: () => setReloadKey((key) => key + 1),
  };
}
